import React, { useState, useEffect, Component, ErrorInfo, ReactNode } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { CssBaseline, Box, Typography, Button, CircularProgress, IconButton } from "@mui/joy";
import { FaMinus, FaPlus } from "react-icons/fa";
import { IconWrapper } from "./components/IconWrapper";
import { CustomerProvider } from "./contexts/CustomerContext";
import { SupabaseAuthProvider, useSupabaseAuth } from "./contexts/SupabaseAuthContext";
import OrderHistory from "./components/OrderHistory";
import WindowDesigner from "./components/WindowDesigner";
import WindowCheckout from "./components/WindowCheckout";
import WindowBasket from "./components/WindowBasket";
import AdminPanel from "./components/AdminPanel";
import { PriceManagement } from "./components/PriceManagement";
import SupplierManagement from "./components/SupplierManagement";
import WindowOrderConfirmation from "./components/WindowOrderConfirmation";
import WindowOrderSuccess from "./components/WindowOrderSuccess";
import Sidebar from "./components/Sidebar";
import ProjectFormPage from './pages/ProjectFormPage';
import ProjectWorkspacePage from './pages/ProjectWorkspacePage';
import WorkspacesDashboardPage from './pages/WorkspacesDashboardPage';
import OpenJobCardsPage from './pages/OpenJobCardsPage';
import FitterPreviewPage from './pages/FitterPreviewPage';
import ProductionDatesPage from './pages/ProductionDatesPage';
import WorkspaceJobRecordsPage from './pages/WorkspaceJobRecordsPage';
import WorkspaceJobRecordDeltaPage from './pages/WorkspaceJobRecordDeltaPage';
import SupabaseLogin from './components/SupabaseLogin';
import EmailActivityDetailPage from './pages/EmailActivityDetailPage';
import AuthTest from './components/AuthTest';
import Expenses from './components/Expenses';
import DirectoryPage from './pages/DirectoryPage';
import DashboardPage from './pages/DashboardPage';
import CallRecordingsPage from './pages/CallRecordingsPage';
import ContactsPage from './pages/ContactsPage';
import FenestrationPage from './pages/FenestrationPage';
import AdditionalCostsDrilldownPage from './pages/AdditionalCostsDrilldownPage';
import CustomerTestPage from './pages/CustomerTestPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import PublicReferralPage from './pages/PublicReferralPage';
import NewsletterUnsubscribePage from './pages/NewsletterUnsubscribePage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useProfile, staffLandingPath } from './hooks/useProfile';
import ProfileTest from './components/ProfileTest';
import AdminUserSetup from './components/AdminUserSetup';
import WorkCalendarPage from './pages/WorkCalendarPage';
import RevenueCalendarPage from './pages/RevenueCalendarPage';
import CisCertificatesPage from './components/CisCertificatesPage';
import FleetDriverPolicyPage from './pages/FleetDriverPolicyPage';
import SiteCrewRecordsGate, { LegacyJobRecordRedirect } from './pages/SiteCrewRecordsGate';
import JobReviewPage from './pages/job-records/JobReviewPage';
import SupplierPortalPage from './pages/SupplierPortalPage';
import DocumentsListPage from './modules/docs/pages/DocumentsListPage';
import EstimateQuoteWizard from './modules/docs/pages/EstimateQuoteWizard';
import DocumentDetailPage from './modules/docs/pages/DocumentDetailPage';
import PublicDocViewerPage from './modules/docs/pages/PublicDocViewerPage';
import OfflinePublicDocPreviewPage from './modules/docs/pages/OfflinePublicDocPreviewPage';
import DocumentTemplateEditor from './modules/docs/components/DocumentTemplateEditor';
import SequenceSettingsPage from './modules/docs/pages/SequenceSettingsPage';
import MigrationPage from './pages/MigrationPage';
import { FreeAgentCallbackPage } from './pages/FreeAgentCallbackPage';
import ActivityNotifications from './components/ActivityNotifications';
import JarvisKanbanPage from './pages/JarvisKanbanPage';
import PricingEnginePage from './pages/pricing-engine/PricingEnginePage';
import InvoicingPage from './modules/accounts/pages/InvoicingPage';
import ArrangementPage from './modules/accounts/pages/ArrangementPage';
import AccountInvoicePage from './modules/accounts/pages/AccountInvoicePage';
import BillsPage from './modules/accounts/pages/BillsPage';
import BillPage from './modules/accounts/pages/BillPage';
import SubcontractorsPage from './modules/accounts/pages/SubcontractorsPage';
import LedgerPage from './modules/ledger/pages/LedgerPage';
import ImagePurifyPage from './pages/WordpressMediaPage';
import LeadsPage from './pages/LeadsPage';
import DoorDesignerPage from './modules/door-designer/pages/DoorDesignerPage';
import DoorOraclePage from './modules/door-oracle/pages/DoorOraclePage';
import { primeOrganisationSettings } from './services/organisationSettings';

// Lazy: the Window Generator editor is ~10k lines and only needed on this route.
const WindowGeneratorStandalonePage = React.lazy(() => import("./pages/WindowGeneratorStandalonePage"));

console.log('App rendered');

// Check if a path segment looks like a share token (base64url, ~43 chars for 32 bytes)
const isShareToken = (segment: string): boolean => {
  if (!segment || segment.length < 40 || segment.length > 50) return false;
  // base64url: alphanumeric + hyphen + underscore, no padding
  return /^[A-Za-z0-9_-]+$/.test(segment);
};

const StaffHome: React.FC = () => {
  const { profile, loading } = useProfile();
  if (loading) return null;
  return <Navigate to={staffLandingPath(profile?.role)} replace />;
};

/** Old project pages (/projects/:id) open the workspace, which holds the job's stage. */
const ProjectWorkspaceRedirect = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`/projects/${encodeURIComponent(projectId || '')}/workspace`} replace />;
};

// Route guard that renders PublicDocViewerPage for token-like root paths
const TokenRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // If it's a single segment that looks like a token, render the public doc viewer
  if (pathSegments.length === 1 && isShareToken(pathSegments[0])) {
    return <PublicDocViewerPage />;
  }

  return <>{children}</>;
};

// Add Error Boundary Component
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography level="h4" color="danger" mb={2}>
            Something went wrong
          </Typography>
          <Typography level="body-md" mb={2}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </Typography>
          <Button
            onClick={() => window.location.reload()}
            variant="solid"
            color="primary"
          >
            Reload Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Component to conditionally render Header and layout based on authentication
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useSupabaseAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = React.useState(false);

  const isDoorDesigner = location.pathname === '/door-designer';
  const isFitterSite = location.pathname.startsWith('/job-records');

  // Don't show sidebar on login page
  if (location.pathname === '/login') {
    return <>{children}</>;
  }

  // Show loading state while checking authentication
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-lg" color="neutral">
          Loading...
        </Typography>
      </Box>
    );
  }

  // If not authenticated, don't show sidebar (user will be redirected to login)
  if (!user) {
    return <>{children}</>;
  }

  // User is authenticated, show responsive layout
  return (
    <>
      {/* Mobile Header with Burger Menu */}
      <Box
        sx={{
          display: { xs: 'flex', lg: 'none' },
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          backgroundColor: 'var(--jarvis-nav-bg)',
          color: 'var(--jarvis-nav-text)',
          borderBottom: '1px solid',
          borderColor: 'var(--jarvis-nav-border)',
          px: 3,
          py: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          // PWA safe area handling
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
          paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 1.5rem)',
          paddingRight: 'calc(env(safe-area-inset-right, 0px) + 1.5rem)',
          paddingBottom: '0.75rem',
          height: 'var(--mobile-header-height, 80px)',
          boxSizing: 'border-box',
        }}
      >
        <Box
          component="img"
          src="/images/brand/jarvis-logo.png"
          alt="JARVIS"
          sx={{
            display: 'block',
            height: 36,
            width: 'auto',
            maxWidth: 160,
            objectFit: 'contain',
            borderRadius: 'sm',
          }}
        />
        <IconButton
          onClick={() => setSidebarOpen(!sidebarOpen)}
          variant="outlined"
          size="sm"
          aria-label={sidebarOpen ? 'Close navigation' : 'Open navigation'}
          sx={{
            color: 'var(--jarvis-nav-text)',
            borderColor: 'var(--jarvis-nav-border)',
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: 'var(--jarvis-nav-hover-bg)',
              color: 'var(--jarvis-nav-hover-text)',
              borderColor: 'var(--jarvis-nav-border)',
            },
          }}
        >
          <IconWrapper icon={sidebarOpen ? FaMinus : FaPlus} />
        </IconButton>
      </Box>

      {/* Sidebar - Hidden on mobile, shown on desktop */}
      <Box
        sx={{
          display: { xs: sidebarOpen ? 'block' : 'none', lg: desktopSidebarCollapsed ? 'none' : 'block' },
          position: 'fixed',
          top: { xs: 'var(--mobile-header-height, 80px)', lg: 0 },
          left: 0,
          zIndex: 1200,
          height: {
            xs: 'calc(100dvh - var(--mobile-header-height, 80px))',
            lg: '100vh',
          },
          width: { xs: '280px', lg: '232px' },
          backgroundColor: 'var(--jarvis-nav-bg)',
          borderRight: '1px solid',
          borderColor: 'var(--jarvis-nav-border)',
          overflow: 'hidden',
          // PWA safe area handling for sidebar
          paddingBottom: { xs: 'env(safe-area-inset-bottom, 0px)', lg: 0 },
        }}
      >
        <Sidebar
          onClose={() => setSidebarOpen(false)}
          onCollapse={() => setDesktopSidebarCollapsed(true)}
        />
      </Box>

      {/* Desktop expand control — slim left rail when nav is collapsed */}
      {desktopSidebarCollapsed && (
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1200,
            width: 48,
            height: '100vh',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: 1.5,
            backgroundColor: 'var(--jarvis-nav-bg)',
            borderRight: '1px solid',
            borderColor: 'var(--jarvis-nav-border)',
          }}
        >
          <IconButton
            onClick={() => setDesktopSidebarCollapsed(false)}
            variant="plain"
            color="neutral"
            size="sm"
            aria-label="Expand navigation"
            sx={{
              color: 'var(--jarvis-nav-text)',
              '&:hover': {
                bgcolor: 'var(--jarvis-nav-hover-bg)',
                color: 'var(--jarvis-nav-hover-text)',
              },
            }}
          >
            <IconWrapper icon={FaPlus} />
          </IconButton>
        </Box>
      )}

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <Box
          sx={{
            display: { xs: 'block', lg: 'none' },
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1100
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <Box 
        sx={{ 
          backgroundColor: 'var(--jarvis-bg-body)',
          color: 'var(--jarvis-text-primary)',
          fontFamily: 'var(--jarvis-font-family)',
          minHeight: '100vh',
          position: 'relative',
          zIndex: 1,
          ml: { xs: 0, lg: desktopSidebarCollapsed ? '48px' : '232px' }, // Desktop: full nav or slim expand rail
          mt: { xs: 'var(--mobile-header-height, 80px)', lg: 0 }, // Add top margin for mobile/tablet fixed header
          pt: { xs: isDoorDesigner ? 0 : 'calc(env(safe-area-inset-top, 0px) + 20px)', lg: isDoorDesigner ? 0 : 2 },
          p: { xs: isDoorDesigner || isFitterSite ? 0 : 1, lg: isDoorDesigner ? 0 : 2 },
          transition: 'margin-left 0.2s ease',
          // PWA safe area handling for content
          paddingLeft: { xs: isDoorDesigner ? 0 : isFitterSite ? 'calc(8px + env(safe-area-inset-left, 0px))' : 'calc(1rem + env(safe-area-inset-left, 0px))', lg: isDoorDesigner ? 0 : 2 },
          paddingRight: { xs: isDoorDesigner ? 0 : isFitterSite ? 'calc(8px + env(safe-area-inset-right, 0px))' : 'calc(1rem + env(safe-area-inset-right, 0px))', lg: isDoorDesigner ? 0 : 2 },
          // Room below the last content for the floating notifications button (fixed bottom-right), so it never covers actions
          paddingBottom: { xs: isDoorDesigner ? 0 : 'calc(80px + env(safe-area-inset-bottom, 0px))', lg: isDoorDesigner ? 0 : '88px' }
        }}
      >
        {children}
      </Box>

      {/* Persistent activity notifications (MSN-style) */}
      <ActivityNotifications />
    </>
  );
};

// FooterSwitcher component to conditionally render the master footer
const FooterSwitcher = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/embed/') || location.pathname.startsWith('/job-records')) {
    return null;
  }
  return (
    <Box 
      component="footer" 
      sx={{ 
        py: 2, 
        px: 3, 
        mt: 'auto', 
        textAlign: 'center',
      }}
    >
      <Typography level="body-sm">
        © {new Date().getFullYear()} Keystone Glazing Ltd
      </Typography>
    </Box>
  );
};

const App: React.FC = () => {
  const [suppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  
  // Brand mailboxes and settings from Admin → Organisations
  useEffect(() => {
    primeOrganisationSettings();
  }, []);

  // Suppress benign ResizeObserver loop errors in Chrome
  useEffect(() => {
    const suppressROError = (event: Event | PromiseRejectionEvent) => {
      const anyEvent: any = event as any;
      const msg: string | undefined = anyEvent?.message || anyEvent?.reason?.message;
      if (typeof msg === 'string' && (msg.includes('ResizeObserver loop limit exceeded') || msg.includes('ResizeObserver loop completed with undelivered notifications'))) {
        event.preventDefault?.();
        // Stop bubbling to React error overlay
        // @ts-ignore
        event.stopImmediatePropagation?.();
        return true;
      }
      return false;
    };
    const onError = (e: ErrorEvent) => { suppressROError(e); };
    const onRejection = (e: PromiseRejectionEvent) => { suppressROError(e); };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection as any);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection as any);
    };
  }, []);

  // Dev-only: filter React overlay by suppressing specific ResizeObserver console errors
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      const first = args[0];
      if (
        typeof first === 'string' && (
          first.includes('ResizeObserver loop limit exceeded') ||
          first.includes('ResizeObserver loop completed with undelivered notifications')
        )
      ) {
        return; // swallow known benign RO warning in dev
      }
      originalConsoleError(...args);
    };
    return () => {
      console.error = originalConsoleError;
    };
  }, []);
  
  return (
    <ErrorBoundary>
      <SupabaseAuthProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <CssBaseline />
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<SupabaseLogin />} />
                <Route path="/access-denied" element={<AccessDeniedPage />} />
                <Route path="/public/doc/preview" element={<OfflinePublicDocPreviewPage />} />
                <Route path="/public/doc/:token" element={<PublicDocViewerPage />} />
                <Route path="/refer" element={<PublicReferralPage />} />
                <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
                {/* Short public doc URL formats */}
                <Route path="/q/:token" element={<PublicDocViewerPage />} />
                <Route path="/d/:token" element={<PublicDocViewerPage />} />

                {/* Authenticated Layout Routes - wrapped in TokenRouteGuard to handle root-level share tokens */}
                <Route path="/*" element={
                  <TokenRouteGuard>
                    <CustomerProvider>
                      <AuthenticatedLayout>
                      <Box 
                        component="main" 
                        sx={{ 
                          flexGrow: 1, 
                          p: 0,
                        }}
                      >
                        <Routes>
                          {/* Protected Routes */}
                          <Route path="/" element={
                            <ProtectedRoute>
                              <StaffHome />
                            </ProtectedRoute>
                          } />
                          {/* Legacy glass calculator routes → Projects home */}
                          <Route path="/calculator" element={<Navigate to="/workspaces" replace />} />
                          <Route path="/basket" element={<Navigate to="/workspaces" replace />} />
                          <Route path="/checkout" element={<Navigate to="/workspaces" replace />} />
                          <Route path="/order-confirmation/:orderId" element={<Navigate to="/workspaces" replace />} />
                          <Route path="/orders" element={
                            <ProtectedRoute>
                              <OrderHistory />
                            </ProtectedRoute>
                          } />
                          <Route path="/invoices" element={<Navigate to="/docs" replace />} />
                          {/* Subcontractor invoices are bills now */}
                          <Route path="/subcontractor-invoices" element={<Navigate to="/bills?tab=all&subcontractors=1" replace />} />
                          <Route path="/invoicing" element={
                            <ProtectedRoute>
                              <InvoicingPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/invoicing/invoices/:documentId" element={
                            <ProtectedRoute>
                              <AccountInvoicePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/invoicing/:ref" element={
                            <ProtectedRoute>
                              <ArrangementPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/bills" element={
                            <ProtectedRoute>
                              <BillsPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/bills/:billId" element={
                            <ProtectedRoute>
                              <BillPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/ledger" element={
                            <ProtectedRoute>
                              <LedgerPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/cis-certificates" element={
                            <ProtectedRoute>
                              <CisCertificatesPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/subcontractors" element={
                            <ProtectedRoute requireStaff>
                              <SubcontractorsPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/fleet-policy" element={
                            <ProtectedRoute>
                              <FleetDriverPolicyPage />
                            </ProtectedRoute>
                          } />
                          {/* JARVIS Documents module */}
                          <Route path="/docs" element={
                            <ProtectedRoute>
                              <DocumentsListPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/docs/new" element={
                            <ProtectedRoute>
                              <EstimateQuoteWizard />
                            </ProtectedRoute>
                          } />
                          <Route path="/docs/new/:projectId" element={
                            <ProtectedRoute>
                              <EstimateQuoteWizard />
                            </ProtectedRoute>
                          } />
                          <Route path="/docs/:documentId" element={
                            <ProtectedRoute>
                              {/* The page will read documentId from route params */}
                              <DocumentDetailPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/docs/edit/:documentId" element={
                            <ProtectedRoute>
                              <EstimateQuoteWizard isEditMode={true} />
                            </ProtectedRoute>
                          } />
                          <Route path="/docs/settings/sequences" element={
                            <ProtectedRoute requireAdmin>
                              <SequenceSettingsPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/docs/templates" element={
                            <ProtectedRoute requireAdmin>
                              <DocumentTemplateEditor />
                            </ProtectedRoute>
                          } />
                          <Route path="/window-designer" element={
                            <ProtectedRoute>
                              <WindowDesigner />
                            </ProtectedRoute>
                          } />
                          <Route path="/window-generator" element={
                            <ProtectedRoute>
                              <React.Suspense fallback={<Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>}>
                                <WindowGeneratorStandalonePage />
                              </React.Suspense>
                            </ProtectedRoute>
                          } />
                          <Route path="/door-designer" element={
                            <ProtectedRoute>
                              <DoorDesignerPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/window-basket" element={
                            <ProtectedRoute>
                              <WindowBasket />
                            </ProtectedRoute>
                          } />
                          
                          <Route path="/window-checkout" element={
                            <ProtectedRoute>
                              <WindowCheckout />
                            </ProtectedRoute>
                          } />
                          <Route path="/admin" element={
                            <ProtectedRoute requireAdmin>
                              <AdminPanel />
                            </ProtectedRoute>
                          } />
                          <Route path="/door-oracle" element={
                            <ProtectedRoute requireAdmin>
                              <DoorOraclePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/prices" element={
                            <ProtectedRoute requireAdmin>
                              <PriceManagement 
                                suppliers={suppliers}
                                selectedSupplier={selectedSupplier}
                                onSupplierChange={setSelectedSupplier}
                              />
                            </ProtectedRoute>
                          } />
                          <Route path="/suppliers" element={
                            <ProtectedRoute requireAdmin>
                              <SupplierManagement />
                            </ProtectedRoute>
                          } />
                          <Route path="/window-order-confirmation/:orderId" element={
                            <ProtectedRoute>
                              <WindowOrderConfirmation />
                            </ProtectedRoute>
                          } />
                          <Route path="/window-order-success" element={
                            <ProtectedRoute>
                              <WindowOrderSuccess />
                            </ProtectedRoute>
                          } />
                          <Route path="/jarvis" element={<Navigate to="/workspaces" replace />} />
                          <Route path="/jarvis/kanban" element={
                            <ProtectedRoute>
                              <JarvisKanbanPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/jarvis/pricing-engine" element={
                            <ProtectedRoute requireAdmin>
                              <PricingEnginePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/jarvis/image-purify" element={
                            <ProtectedRoute>
                              <ImagePurifyPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/jarvis/media" element={
                            <ProtectedRoute>
                              <Navigate to="/jarvis/image-purify" replace />
                            </ProtectedRoute>
                          } />
                          <Route path="/leads" element={
                            <ProtectedRoute>
                              <LeadsPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspaces/list" element={
                            <ProtectedRoute>
                              <WorkspacesDashboardPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspaces/job-cards" element={
                            <ProtectedRoute>
                              <OpenJobCardsPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspaces/fitter-view" element={
                            <ProtectedRoute>
                              <FitterPreviewPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspaces/production-dates" element={
                            <ProtectedRoute>
                              <ProductionDatesPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspaces/job-records" element={
                            <ProtectedRoute>
                              <WorkspaceJobRecordsPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspaces/job-records/:projectId" element={
                            <ProtectedRoute>
                              <WorkspaceJobRecordDeltaPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspaces" element={
                            <ProtectedRoute>
                              <WorkspacesDashboardPage />
                            </ProtectedRoute>
                          } />
                          {/* The old project list and board: Workspaces is the one view of where jobs are */}
                          <Route path="/projects" element={<Navigate to="/workspaces" replace />} />
                          <Route path="/projects-board" element={<Navigate to="/workspaces" replace />} />
                          <Route path="/projects/new" element={
                            <ProtectedRoute>
                              <ProjectFormPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/projects/:projectId/edit" element={
                            <ProtectedRoute>
                              <ProjectFormPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/projects/:projectId/workspace" element={
                            <ProtectedRoute>
                              <ProjectWorkspacePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/projects/:projectId/workspace/:stage" element={
                            <ProtectedRoute>
                              <ProjectWorkspacePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/projects/:projectId" element={<ProjectWorkspaceRedirect />} />
                          <Route path="/email-activity/:emailId" element={
                            <ProtectedRoute>
                              <EmailActivityDetailPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/auth-test" element={
                            <ProtectedRoute>
                              <AuthTest />
                            </ProtectedRoute>
                          } />
                          <Route path="/expenses" element={
                            <ProtectedRoute>
                              <Expenses />
                            </ProtectedRoute>
                          } />
                          <Route path="/directory" element={
                            <ProtectedRoute>
                              <DirectoryPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/customers/:customerName" element={
                            <ProtectedRoute>
                              <CustomerDetailPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/dashboard" element={
                            <ProtectedRoute>
                              <DashboardPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/call-recordings" element={
                            <ProtectedRoute>
                              <CallRecordingsPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/contacts" element={
                            <ProtectedRoute>
                              <ContactsPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/fenestration" element={
                            <ProtectedRoute>
                              <FenestrationPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/dashboard/additional-costs" element={
                            <ProtectedRoute>
                              <AdditionalCostsDrilldownPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/customer-test" element={
                            <ProtectedRoute>
                              <CustomerTestPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/profile-test" element={
                            <ProtectedRoute>
                              <ProfileTest />
                            </ProtectedRoute>
                          } />
                          <Route path="/admin-setup" element={
                            <ProtectedRoute requireAdmin>
                              <AdminUserSetup />
                            </ProtectedRoute>
                          } />
                          <Route path="/work-calendar" element={
                            <ProtectedRoute>
                              <WorkCalendarPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/revenue-calendar" element={
                            <ProtectedRoute>
                              <RevenueCalendarPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/job-records" element={
                            <ProtectedRoute>
                              <SiteCrewRecordsGate />
                            </ProtectedRoute>
                          } />
                          <Route path="/job-records/:projectId" element={
                            <ProtectedRoute>
                              <SiteCrewRecordsGate />
                            </ProtectedRoute>
                          } />
                          <Route path="/job-records/review/:projectId" element={
                            <ProtectedRoute>
                              <JobReviewPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/job-records/:projectId/:invoiceId" element={
                            <ProtectedRoute>
                              <LegacyJobRecordRedirect />
                            </ProtectedRoute>
                          } />
                          <Route path="/supplier-portal" element={
                            <ProtectedRoute>
                              <SupplierPortalPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/migration" element={
                            <ProtectedRoute>
                              <MigrationPage />
                            </ProtectedRoute>
                          } />
                          {/* FreeAgent OAuth callback - no protection needed */}
                          <Route path="/integrations/freeagent/callback" element={<FreeAgentCallbackPage />} />
                        </Routes>
                      </Box>
                      </AuthenticatedLayout>
                    </CustomerProvider>
                  </TokenRouteGuard>
                } />
              </Routes>
              
              <FooterSwitcher />
            </Box>
        </BrowserRouter>
      </SupabaseAuthProvider>
    </ErrorBoundary>
  );
};

export default App;
